import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  getDataObjectProvenance,
  DataObjectProvenanceResponse,
  LineageDatasetIdentity,
  LineageRunIdentity,
} from '../utils/ontologyApi';

type DatasetProvenancePanelProps = {
  dataObjectId: string;
  version: number;
  datasetName?: string;
};

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const runStatusColor = (
  status?: string | null
): 'success' | 'error' | 'info' | 'warning' | 'default' => {
  switch ((status || '').toUpperCase()) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
    case 'ABORTED':
      return 'error';
    case 'RUNNING':
    case 'STARTED':
      return 'info';
    case 'OTHER':
      return 'warning';
    default:
      return 'default';
  }
};

const datasetLabel = (dataset?: LineageDatasetIdentity | null): string => {
  if (!dataset) {
    return 'Unknown dataset';
  }
  const name = dataset.name || dataset.dataObjectId || dataset.lineageDatasetId || 'Unknown dataset';
  return dataset.dataObjectVersion != null ? `${name} (v${dataset.dataObjectVersion})` : name;
};

const DatasetList: React.FC<{ label: string; datasets?: LineageDatasetIdentity[] | null }> = ({
  label,
  datasets,
}) => {
  if (!datasets || datasets.length === 0) {
    return null;
  }
  return (
    <Box sx={{ pl: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        {datasets.map((dataset, index) => (
          <Chip
            key={`${label}-${dataset.lineageDatasetId || dataset.dataObjectId || index}`}
            size="small"
            variant="outlined"
            label={datasetLabel(dataset)}
          />
        ))}
      </Stack>
    </Box>
  );
};

const RunLine: React.FC<{ run?: LineageRunIdentity | null }> = ({ run }) => {
  if (!run) {
    return null;
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {run.status && <Chip size="small" label={run.status} color={runStatusColor(run.status)} />}
      <Typography variant="caption" color="text.secondary">
        {run.finishedAt
          ? `Finished ${formatDateTime(run.finishedAt)}`
          : run.startedAt
            ? `Started ${formatDateTime(run.startedAt)}`
            : 'No run timestamp'}
      </Typography>
    </Stack>
  );
};

/**
 * Dataset-level provenance panel: answers "where did this dataset/score come from".
 * Reads the canonical-lineage provenance endpoint (tenant-scoped server-side) for a specific
 * data-object version and renders the producer runs (with their input datasets) plus the
 * downstream consumers. Read-only; never mutates lineage.
 */
const DatasetProvenancePanel: React.FC<DatasetProvenancePanelProps> = ({
  dataObjectId,
  version,
  datasetName,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [notAvailable, setNotAvailable] = useState(false);
  const [data, setData] = useState<DataObjectProvenanceResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!dataObjectId || version == null) {
      setData(null);
      setError(null);
      setForbidden(false);
      setNotAvailable(false);
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      setError(null);
      setForbidden(false);
      setNotAvailable(false);
      try {
        const response = await getDataObjectProvenance(dataObjectId, version);
        if (!cancelled) {
          setData(response);
        }
      } catch (e: any) {
        if (cancelled) {
          return;
        }
        setData(null);
        const status = e?.response?.status;
        if (status === 403) {
          setForbidden(true);
        } else if (status === 404) {
          // Non-disclosure contract can 404 for cross-tenant / unknown objects. Show the same
          // neutral "no provenance" state as an empty result so it is indistinguishable.
          setNotAvailable(true);
        } else {
          setError(
            e?.response?.data?.message || e?.message || 'Failed to load provenance for this dataset.'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [dataObjectId, version]);

  const producerRuns = data?.producerRuns ?? [];
  const downstreamConsumers = data?.downstreamConsumers ?? [];
  const isEmpty =
    notAvailable ||
    (!!data && !data.hasProvenance && producerRuns.length === 0 && downstreamConsumers.length === 0);

  return (
    <Stack spacing={1} data-testid="dataset-provenance-panel">
      <Typography variant="subtitle2">Provenance</Typography>
      <Typography variant="caption" color="text.secondary">
        Where {datasetName ? `“${datasetName}”` : 'this dataset'} (v{version}) came from and what it
        feeds.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && forbidden && (
        <Alert severity="info">
          You do not have permission to view provenance for this dataset.
        </Alert>
      )}

      {!loading && !error && !forbidden && isEmpty && (
        <Typography variant="body2" color="text.secondary">
          No provenance recorded for this dataset version yet. Provenance appears once a
          transformation or pipeline run produces or consumes it.
        </Typography>
      )}

      {!loading && !error && !forbidden && !isEmpty && data && (
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Produced by
            </Typography>
            {producerRuns.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No producing run recorded — this dataset was uploaded directly or its producer
                predates lineage capture.
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {producerRuns.map((producer, index) => (
                  <Box
                    key={`producer-${producer.run?.runId || producer.job?.jobId || index}`}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                  >
                    <Typography variant="body2">
                      {producer.job?.jobName || 'Unnamed job'}
                      {producer.job?.jobKind ? ` · ${producer.job.jobKind}` : ''}
                    </Typography>
                    <RunLine run={producer.run} />
                    <DatasetList label="Built from inputs:" datasets={producer.inputDatasets} />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Used by
            </Typography>
            {downstreamConsumers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing downstream consumes this dataset version yet.
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 0.5 }}>
                {downstreamConsumers.map((consumer, index) => (
                  <Box
                    key={`consumer-${consumer.run?.runId || consumer.job?.jobId || index}`}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                  >
                    <Typography variant="body2">
                      {consumer.job?.jobName || 'Unnamed job'}
                      {consumer.job?.jobKind ? ` · ${consumer.job.jobKind}` : ''}
                    </Typography>
                    <RunLine run={consumer.run} />
                    <DatasetList label="Produces outputs:" datasets={consumer.outputDatasets} />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {data.evidenceLabels && data.evidenceLabels.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Evidence:{' '}
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.5 }}
                component="span"
              >
                {data.evidenceLabels.map((label) => (
                  <Chip key={`evidence-${label}`} size="small" variant="outlined" label={label} />
                ))}
              </Stack>
            </Box>
          )}

          <Link
            href="/lineage"
            variant="caption"
            underline="hover"
            sx={{ alignSelf: 'flex-start' }}
          >
            View full pipeline lineage
          </Link>
        </Stack>
      )}
    </Stack>
  );
};

export default DatasetProvenancePanel;
